import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import type { DomainChallenge, DomainVerifyResponse } from "../api/types";
import { formatLocal } from "../lib/datetime";
import { Banner, Button, CopyField, QueryView, StatusPill } from "./ui";

// DNS-01 domain-control proof (ARCHITECTURE.md §3/§8; UX-DESIGN.md §3.1
// step 2), shared by publisher sites and advertiser creative destinations
// (cmd/core-platform/domain_control.go). The check runs synchronously on
// "Verify"; a record that isn't visible yet is an ordinary state
// ("not_verified" / challenge_record_not_found), not a failure.
//
// Not covered by the API yet (API-GAPS.md G-32): challenge expiry, a
// generic ownership-conflict outcome, and periodic re-verification status.

interface Props {
  queryKey: readonly unknown[];
  load: () => Promise<DomainChallenge>;
  verify: () => Promise<DomainVerifyResponse>;
  /** What is being verified, for copy ("site", "destination"). */
  subject: string;
  onVerified?: (res: DomainVerifyResponse) => void;
}

export function DomainVerification({ queryKey, load, verify, subject, onVerified }: Props) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey, queryFn: load });
  const [lastAttempt, setLastAttempt] = useState<{ at: Date; res: DomainVerifyResponse } | null>(null);

  const m = useMutation({
    mutationFn: verify,
    onSuccess: (res) => {
      setLastAttempt({ at: new Date(), res });
      if (res.applied || res.result === "reverified" || res.result === "verified" || res.result === "activated") {
        void qc.invalidateQueries({ queryKey });
        onVerified?.(res);
      }
    },
  });

  return (
    <QueryView query={q} loadingLabel="Loading verification record">
      {(ch) => {
        const verified = !!ch.verified_at;
        return (
          <div className="stack">
            <div className="row">
              {verified ? (
                <StatusPill tone="success">Verified {formatLocal(ch.verified_at!)}</StatusPill>
              ) : (
                <StatusPill tone="warning">Not verified yet</StatusPill>
              )}
            </div>
            {!verified ? (
              <p className="small">
                At your DNS provider, add a <strong>{ch.record_type}</strong> record for this {subject}:
              </p>
            ) : null}
            <div className="stack-sm">
              <span className="field-label">Record name</span>
              <CopyField label="DNS record name" value={ch.record_name} />
              <span className="field-label">Record type</span>
              <code>{ch.record_type}</code>
              <span className="field-label">Record value</span>
              <CopyField label="DNS record value" value={ch.record_value} />
            </div>
            {!verified ? (
              <p className="small muted">
                Then choose Verify. New DNS records can take up to a few hours to be visible — if it isn't found yet, wait and
                try again; nothing is lost.
              </p>
            ) : null}
            {m.isError ? (
              <Banner tone="error" live="alert">
                {m.error instanceof Error ? m.error.message : "Verification couldn't run."}
              </Banner>
            ) : null}
            {lastAttempt && !m.isPending ? <AttemptResult res={lastAttempt.res} at={lastAttempt.at} /> : null}
            <div>
              <Button variant={verified ? "secondary" : "primary"} onClick={() => m.mutate()} loading={m.isPending}>
                {verified ? "Re-check now" : "Verify"}
              </Button>
            </div>
          </div>
        );
      }}
    </QueryView>
  );
}

function AttemptResult({ res, at }: { res: DomainVerifyResponse; at: Date }) {
  const when = at.toLocaleTimeString();
  if (res.result === "not_verified") {
    return (
      <Banner tone="warning" live="polite" title="Record not found yet">
        Checked at {when}. We couldn't see the TXT record yet. DNS changes can take up to a few hours to propagate — check the
        name and value match exactly, then try again later.
      </Banner>
    );
  }
  if (res.result === "activated") {
    return (
      <Banner tone="success" live="polite" title="Domain verified">
        Checked at {when}. Domain control is proven.
      </Banner>
    );
  }
  if (res.applied || res.result === "verified" || res.result === "reverified") {
    return (
      <Banner tone="success" live="polite" title="Domain verified">
        Checked at {when}.
      </Banner>
    );
  }
  return (
    <Banner tone="warning" live="polite" title="This domain could not be verified">
      Checked at {when}. It may already be registered to another account; contact support if you believe this is an error.
    </Banner>
  );
}
